import { Badge, Button, cn, Icon, NotProvided } from '@evogard/ui';
import type { Peripheral, PeripheralItem, PeripheralQuote } from '@evogard/contract';
import {
  PERIPHERAL_AVAILABILITIES,
  PERIPHERAL_PART_ORIGINS,
  PERIPHERAL_QUOTE_STATUSES,
  count,
  format,
  formatDate,
  fromColumn,
  peripheralItemTypeLabel,
} from '@evogard/domain';

/**
 * O quadro de cotações — o miolo da mesa na Cotação e na Aprovação do
 * Supervisor (15/10/2026). Até então o miolo era só a contagem de itens, e as
 * cotações moravam na gaveta da lateral, uma embaixo da outra.
 *
 * Um cartão por item, e dentro dele as cotações LADO A LADO, da menor para a
 * maior: é uma comparação, e comparação se lê na horizontal. A de menor valor
 * e a de preferência de compra carregam o selo; a aprovada, o contorno verde.
 * Cadastrar é a peça tracejada no fim da fila, onde a próxima cotação vai
 * ficar. Aprovar e reprovar só aparecem para quem supervisiona, na Aprovação.
 *
 * A gaveta continua existindo para o que não é cotação: o questionário da
 * peça e as fotos ("Detalhes da peça").
 */
export function PeripheralQuoteBoard({
  peripheral: p,
  quoting,
  approving,
  busy,
  highlighted,
  onAddItem,
  onDetails,
  onNewQuote,
  onEditQuote,
  onDecide,
}: {
  peripheral: Peripheral;
  /** Na Cotação, com permissão: cadastrar e corrigir. */
  quoting: boolean;
  /** Na Aprovação, para quem supervisiona: aprovar e reprovar. */
  approving: boolean;
  busy: boolean;
  /** O item escolhido na coluna da esquerda. */
  highlighted: number | null;
  onAddItem?: () => void;
  onDetails: (itemId: number) => void;
  onNewQuote: (itemId: number) => void;
  onEditQuote: (itemId: number, quote: PeripheralQuote) => void;
  onDecide: (quoteId: number, decision: 'aprovado' | 'reprovado') => void;
}) {
  const approved = p.items.filter((i) => i.quotes.some((q) => q.status === 'aprovado'));
  const totals = p.totals;

  return (
    <section aria-labelledby="quadro-cotacoes" className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 id="quadro-cotacoes" className="text-base font-semibold text-texto">
            Cotações dos itens
          </h3>
          <p className="text-sm text-texto-suave">
            {p.items.length === 0
              ? 'Nenhum item acionado ainda.'
              : `${count(p.items.length, 'item', 'itens')} · ${approved.length} com cotação aprovada`}
          </p>
        </div>
        {/* A conta é da API: aberta, ela diz por quê numa frase; fechada, os três números. */}
        {totals.ok ? (
          <dl className="flex flex-wrap gap-2">
            <Total label="Total aprovado" value={money(totals.totalApproved)} />
            <Total label="Associação paga" value={money(totals.associationShare)} />
            <Total label="Associado paga (PI)" value={money(totals.memberShare)} strong />
          </dl>
        ) : (
          p.items.length > 0 && (
            <p className="max-w-64 text-right text-xs text-texto-suave">
              {totals.message}
            </p>
          )
        )}
      </header>

      {p.items.length === 0 && onAddItem && (
        <button
          type="button"
          onClick={onAddItem}
          className={cn(
            'flex min-h-24 flex-col items-center justify-center gap-1 rounded-borda border-2 border-dashed border-borda-forte',
            'text-sm font-medium text-texto-suave transition-colors hover:border-texto-suave hover:bg-superficie-suave hover:text-texto',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
          )}
        >
          <Icon nome="mais" size={20} />
          Acionar o primeiro item
        </button>
      )}

      <ol className="flex flex-col gap-4">
        {p.items.map((item, index) => (
          <li key={item.id}>
            <ItemCard
              item={item}
              position={index + 1}
              quoting={quoting}
              approving={approving}
              busy={busy}
              highlighted={highlighted === item.id}
              onDetails={() => onDetails(item.id)}
              onNewQuote={() => onNewQuote(item.id)}
              onEditQuote={(quote) => onEditQuote(item.id, quote)}
              onDecide={onDecide}
            />
          </li>
        ))}
      </ol>

      {!approving &&
        p.items.some((i) =>
          i.quotes.some((q) => q.status === 'aguardando_aprovacao'),
        ) && (
          <p className="text-xs text-texto-suave">
            Aprovar e reprovar é do supervisor, na Aprovação do Supervisor.
          </p>
        )}
    </section>
  );
}

/** A âncora do cartão do item, para a coluna da esquerda levar até ele. */
export function quoteBoardAnchor(itemId: number): string {
  return `cotacoes-item-${itemId}`;
}

function Total({
  label,
  value,
  strong,
}: {
  label: string;
  value: string | null;
  strong?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col rounded-borda border px-3 py-1.5',
        strong ? 'border-primaria-texto/40 bg-primaria-suave' : 'border-borda',
      )}
    >
      <dt className="text-[0.6875rem] font-medium uppercase tracking-wide text-texto-suave">
        {label}
      </dt>
      <dd className="text-sm font-semibold tabular-nums text-texto">
        {value ?? <NotProvided />}
      </dd>
    </div>
  );
}

function ItemCard({
  item,
  position,
  quoting,
  approving,
  busy,
  highlighted,
  onDetails,
  onNewQuote,
  onEditQuote,
  onDecide,
}: {
  item: PeripheralItem;
  position: number;
  quoting: boolean;
  approving: boolean;
  busy: boolean;
  highlighted: boolean;
  onDetails: () => void;
  onNewQuote: () => void;
  onEditQuote: (quote: PeripheralQuote) => void;
  onDecide: (quoteId: number, decision: 'aprovado' | 'reprovado') => void;
}) {
  const sorted = [...item.quotes].sort((a, b) => {
    const va = fromColumn(a.quotedValue);
    const vb = fromColumn(b.quotedValue);
    if (va === null) return 1;
    if (vb === null) return -1;
    return va - vb;
  });
  const approvedQuote = item.quotes.find((q) => q.status === 'aprovado');
  const label = peripheralItemTypeLabel(item.itemType);

  return (
    <article
      id={quoteBoardAnchor(item.id)}
      aria-label={label}
      className={cn(
        'scroll-mt-4 rounded-borda border bg-superficie transition-shadow',
        highlighted ? 'border-primaria-texto shadow-md' : 'border-borda',
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-borda-suave px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden="true"
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-superficie-suave text-xs font-semibold tabular-nums text-texto-suave"
          >
            {position}
          </span>
          <div className="flex min-w-0 flex-col">
            <h4 className="truncate text-sm font-semibold text-texto">{label}</h4>
            <span className="text-xs text-texto-suave">
              {item.quotes.length === 0
                ? 'Sem cotação'
                : count(item.quotes.length, 'cotação', 'cotações')}
              {item.preExistingDamage ? ' · avaria pré-existente' : ''}
            </span>
          </div>
          {approvedQuote ? (
            <Badge tone="sucesso">
              Aprovada · {money(approvedQuote.quotedValue) ?? 'sem valor'}
            </Badge>
          ) : item.quotes.some((q) => q.status === 'aguardando_aprovacao') ? (
            <Badge tone="alerta">Aguardando aprovação</Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="discreto" size="sm" icon="documento" onClick={onDetails}>
            Detalhes da peça
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(13.5rem,1fr))] gap-3 p-4">
        {sorted.map((q) => (
          <QuoteTile
            key={q.id}
            quote={q}
            quoting={quoting}
            approving={approving}
            busy={busy}
            onEdit={() => onEditQuote(q)}
            onDecide={(decision) => onDecide(q.id, decision)}
          />
        ))}
        {quoting ? (
          <button
            type="button"
            onClick={onNewQuote}
            className={cn(
              'flex min-h-36 flex-col items-center justify-center gap-1.5 rounded-borda border-2 border-dashed border-borda-forte p-3',
              'text-sm font-medium text-texto-suave transition-colors hover:border-texto-suave hover:bg-superficie-suave hover:text-texto',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
            )}
          >
            <Icon nome="mais" size={20} />
            {sorted.length === 0 ? 'Adicionar a primeira cotação' : 'Adicionar cotação'}
          </button>
        ) : (
          sorted.length === 0 && (
            <p className="text-sm text-texto-suave">
              Nenhuma cotação. Elas se cadastram na Cotação.
            </p>
          )
        )}
      </div>
    </article>
  );
}

const money = (value: string | null) => {
  const m = fromColumn(value);
  return m === null ? null : format(m);
};

function labelOf<T extends Record<string, string>>(map: T, code: string | null) {
  return code ? ((map as Record<string, string>)[code] ?? code) : null;
}

/** Uma cotação no quadro: o valor em destaque, quem cotou e o que ela traz. */
function QuoteTile({
  quote: q,
  quoting,
  approving,
  busy,
  onEdit,
  onDecide,
}: {
  quote: PeripheralQuote;
  quoting: boolean;
  approving: boolean;
  busy: boolean;
  onEdit: () => void;
  onDecide: (decision: 'aprovado' | 'reprovado') => void;
}) {
  const approved = q.status === 'aprovado';
  const rejected = q.status === 'reprovado';
  const details = [
    labelOf(PERIPHERAL_PART_ORIGINS, q.partOrigin),
    labelOf(PERIPHERAL_AVAILABILITIES, q.availability),
    q.expectedArrival ? `chega ${formatDate(q.expectedArrival)}` : null,
  ].filter(Boolean);

  return (
    <div
      className={cn(
        'flex min-h-36 flex-col gap-2 rounded-borda border p-3',
        approved && 'border-sucesso bg-sucesso-suave/40 ring-1 ring-sucesso',
        rejected && 'border-borda-suave opacity-60',
        !approved && !rejected && 'border-borda',
      )}
    >
      <span
        className={cn(
          'text-lg font-semibold tabular-nums text-texto',
          rejected && 'line-through',
        )}
      >
        {money(q.quotedValue) ?? (
          <span className="text-sm font-normal text-texto-suave">Sem valor</span>
        )}
      </span>

      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-texto">
          {q.providerName ?? q.vendorName ?? 'Fornecedor não informado'}
        </span>
        {(q.brand || details.length > 0) && (
          <span className="text-xs text-texto-suave">
            {[q.brand, ...details].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Badge tone={approved ? 'sucesso' : rejected ? 'erro' : 'neutro'}>
          {labelOf(PERIPHERAL_QUOTE_STATUSES, q.status)}
        </Badge>
        {q.lowest && <Badge tone="informacao">Menor valor</Badge>}
        {q.purchasePreference && <Badge tone="progresso">Preferência de compra</Badge>}
      </div>

      {(quoting || approving) && (
        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          {quoting && !approved && (
            <Button variant="secundario" size="sm" onClick={onEdit}>
              Corrigir
            </Button>
          )}
          {approving && !approved && q.quotedValue !== null && (
            <Button size="sm" loading={busy} onClick={() => onDecide('aprovado')}>
              Aprovar
            </Button>
          )}
          {approving && !rejected && (
            <Button
              variant="contorno"
              size="sm"
              disabled={busy}
              onClick={() => onDecide('reprovado')}
            >
              Reprovar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
